/* eslint-disable react-hooks/rules-of-hooks */
import React, { useEffect, useState } from 'react';
import { DataGridPro, GridToolbarContainer, gridClasses } from '@mui/x-data-grid-pro';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import useDialogHook from 'hooks/useDialogHook';

import { useTheme } from '@mui/material/styles';
import { grey } from '@mui/material/colors';
import Stack from '@mui/material/Stack';
// Core elements
import IconButton from '@mui/material/IconButton';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import Modal from '@mui/material/Modal';
import Avatar from '@mui/material/Avatar';
import { TableFooter, TableCell, TableRow } from '@mui/material';
import MuiLink from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';

// Icons
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import HistoryIcon from '@mui/icons-material/History';
import PowerOffIcon from '@mui/icons-material/PowerOff';
import GoogleIcon from '@mui/icons-material/Google';
import SettingsIcon from '@mui/icons-material/Settings';

// forms
import { UKasa } from 'layouts/projects/Forms/UKasa/UKasaForm';
import { Kasa } from 'layouts/projects/Forms/Kasa/KasaForm';
import { Kanat } from 'layouts/projects/Forms/Kanat/KanatForm';
import { UPervaz } from 'layouts/projects/Forms/UPervaz/UPervazForm';
import { Pervaz } from 'layouts/projects/Forms/Pervaz/PervazForm';
import { Diger } from 'layouts/projects/Forms/Diger/DigerForm';
import { updateDiger, updateKanat, updateKasa, updatePervaz, updateUKasa, updateUPervaz } from 'services/api/form';
import { deleteUKasa, deleteKasa, deleteKanat, deleteUPervaz, deletePervaz, deleteDiger } from 'services/api/form';
import { copyUKasa, copyKasa, copyKanat, copyUPervaz, copyPervaz, copyDiger } from 'services/api/form';
// import { updateDemand } from 'services/api/project';
// form summaries
// import KasaGrid from 'layouts/projects/Forms/Kasa/KasaGrid';
// import UKasaGrid from 'layouts/projects/Forms/UKasa/UKasaGrid';
// import PervazGrid from 'layouts/projects/Forms/Pervaz/PervazGrid';
// import UPervazGrid from 'layouts/projects/Forms/UPervaz/UPervazGrid';
// import KanatGrid from 'layouts/projects/Forms/Kanat/KanatGrid';
import Comparision from 'layouts/projects/Forms/Comparision';

// form histories
import FormHistory from 'layouts/projects/Forms/FormHistoy';

// helpers
import { calculateDiscount, formatPrice } from 'helpers/priceHelpers';
import { getDemandStatusColor } from 'helpers/colorHelpers';
import { notify } from 'helpers/notificationHelper';

import pxToRem from 'assets/theme/functions/pxToRem.js';

import { getTags } from 'services/api/general/tag';
import { config } from 'Constants';
import AaroLoadingScreen from 'components/Loading';
import {
    lineImage,
    renderCellExpand,
    demandNamesWeWant,
    richTextTheme,
    cellStyles,
    getModalStyle,
} from './basicHelper';
import { LicenseInfo } from '@mui/x-license';

LicenseInfo.setLicenseKey(
    'dae76893fbeff9470d7d5b807a40e544Tz0xMTM0NjQsRT0xNzc5NjY3MTk5MDAwLFM9cHJvLExNPXN1YnNjcmlwdGlvbixQVj1RMy0yMDI0LEtWPTI='
);

const fetchTags = async (id) => {
    const response = await getTags(id).then((rp) => (rp.data?.result?.tags ? rp.data?.result?.tags : []));
    return response;
};

const TAG_GROUP_ID_DEMAND = config.fixedID.DEMAND_STATUS_TAG_GROUP_ID;

// Custom Toolbar Component
function CustomToolbar({ expandRows, setExpandRows, onBulkDiscountChange }) {
    return (
        <GridToolbarContainer
            sx={{
                borderBottom: '1px solid',
                borderColor: 'divider',
                py: 1,
                justifyContent: 'flex-start',
            }}
        >
            <Stack direction="row" spacing={1}>
                <MDButton variant="outlined" size="small" color="dark" onClick={() => setExpandRows(!expandRows)}>
                    {expandRows ? 'DARALT' : 'GENİŞLET'}
                </MDButton>

                <MDButton variant="outlined" size="small" color="dark" onClick={onBulkDiscountChange}>
                    iskonto
                </MDButton>
            </Stack>
        </GridToolbarContainer>
    );
}

export default function RenderCellGrid(props) {
    const { projectID, demand, projectName } = props;
    const { open: openComparision, handleClickOpen, handleClose } = useDialogHook();
    const queryClient = useQueryClient();
    const user = JSON.parse(localStorage.getItem('user'));
    const [expandRows, setExpandRows] = useState(false); // Başlangıçta dar, kullanıcı genişletebilir
    const [rowsUpdated, setRowsUpdated] = useState(false);
    const [rowVals, setRowVals] = useState([]);
    const [modalStyle] = React.useState(getModalStyle);
    const theme = useTheme();

    const [open, setOpen] = React.useState(false);
    const [openHistory, setOpenHistory] = React.useState(false);
    const [openBulkDiscount, setOpenBulkDiscount] = React.useState(false);
    const [bulkDiscountValue, setBulkDiscountValue] = React.useState('');
    const [activeItemID, setActiveItemID] = React.useState('');
    const [activeItemName, setActiveItemName] = React.useState('');
    const [activeItemData, setActiveItemData] = React.useState({});
    const [notes, setNotes] = React.useState('');
    const [loading, setLoading] = React.useState(false);
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [selectedRowId, setSelectedRowId] = React.useState(null);

    const {
        data: tags,
        isLoading,
        error,
    } = useQuery({
        queryKey: ['fetchTagGroups', TAG_GROUP_ID_DEMAND],
        queryFn: () => fetchTags(TAG_GROUP_ID_DEMAND),
        staleTime: 600000,
    });

    // * Edit cells section
    const updateCell = React.useCallback((type, id, obj, silent = false) => {
        if (type === 'kanat')
            updateKanat(id, obj)
                .then((rp) => {
                    if (!silent) notify(rp.data.success, rp.data.message);
                    queryClient.invalidateQueries({ queryKey: ['fetchDemand', demand._id] });
                })
                .catch((err) => {
                    if (!silent) notify(false, err.message);
                });
        else if (type === 'diger')
            updateDiger(id, obj)
                .then((rp) => {
                    if (!silent) notify(rp.data.success, rp.data.message);
                    queryClient.invalidateQueries({ queryKey: ['fetchDemand', demand._id] });
                })
                .catch((err) => {
                    if (!silent) notify(false, err.message);
                });
        else if (type === 'kasa')
            updateKasa(id, obj)
                .then((rp) => {
                    if (!silent) notify(rp.data.success, rp.data.message);
                    queryClient.invalidateQueries({ queryKey: ['fetchDemand', demand._id] });
                })
                .catch((err) => {
                    if (!silent) notify(false, err.message);
                });
        else if (type === 'uKasa')
            updateUKasa(id, obj)
                .then((rp) => {
                    if (!silent) notify(rp.data.success, rp.data.message);
                    queryClient.invalidateQueries({ queryKey: ['fetchDemand', demand._id] });
                })
                .catch((err) => {
                    if (!silent) notify(false, err.message);
                });
        else if (type === 'pervaz')
            updatePervaz(id, obj)
                .then((rp) => {
                    if (!silent) notify(rp.data.success, rp.data.message);
                    queryClient.invalidateQueries({ queryKey: ['fetchDemand', demand._id] });
                })
                .catch((err) => {
                    if (!silent) notify(false, err.message);
                });
        else if (type === 'uPervaz')
            updateUPervaz(id, obj)
                .then((rp) => {
                    if (!silent) notify(rp.data.success, rp.data.message);
                    queryClient.invalidateQueries({ queryKey: ['fetchDemand', demand._id] });
                })
                .catch((err) => {
                    if (!silent) notify(false, err.message);
                });
    }, []);

    const processRowUpdate = React.useCallback(
        (newRow, oldRow) => {
            const id = newRow.id.split('-')[0];
            const type = newRow.id.split('-')[1];

            // Find what field changed
            const changedField = Object.keys(newRow).find((key) => newRow[key] !== oldRow[key]);

            if (changedField) {
                const value = newRow[changedField];
                let obj = {};
                obj[changedField] = value;

                if (changedField === 'durumu') {
                    const findTagID = tags.find((tag) => tag.name === value);
                    obj[changedField] = findTagID._id;
                }

                updateCell(type, id, obj);
            }

            return newRow;
        },
        [tags, updateCell]
    );

    const handleProcessRowUpdateError = React.useCallback((error) => {
        notify(false, error.message);
    }, []);

    useEffect(() => {
        let rowValues = [];
        const allDemandItems = demand;
        let isEmptyCounter = 0;
        Object.keys(allDemandItems).forEach((demandName) => {
            if (demandNamesWeWant.includes(demandName)) {
                // array demands arrayi keyleri birden fazlaysa rowlar boş değildir
                isEmptyCounter = isEmptyCounter + allDemandItems[demandName].length;
                for (let j = 0; j < allDemandItems[demandName].length; j++) {
                    const singleDemandItem = allDemandItems[demandName][j];
                    // console.log(singleDemandItem);
                    let googleSheetURL = '';
                    if (demand.activeSheet) {
                        const { matches, spreadsheetId } = demand.activeSheet;
                        const match = matches.find((el) => el.productID === singleDemandItem._id);
                        googleSheetURL = `https://docs.google.com/spreadsheets/d/${spreadsheetId}/edit#gid=${match?.sheetId}`;
                    }

                    const rowValue = {
                        id: `${singleDemandItem._id}-${demandName}`,
                        googleSheetURL: googleSheetURL,
                        talepFormununAdi: demandName,
                        yoneticiNotu: singleDemandItem.yoneticiNotu,
                        en: singleDemandItem.en,
                        boy: singleDemandItem.boy,
                        kalinlik: singleDemandItem.kalinlik,
                        gorsel: singleDemandItem.gorsel ? singleDemandItem.gorsel : lineImage(demandName),
                        baslik: singleDemandItem.baslik,
                        ekNot: singleDemandItem.ekNot,
                        durumu: singleDemandItem?.durumu?.name,
                        verilenFiyat: singleDemandItem.verilenFiyat,
                        miktar: singleDemandItem.miktar,
                        indirimOrani: singleDemandItem.indirimOrani ? singleDemandItem.indirimOrani : 0,
                        toplam: 0,
                        musteriNotu: singleDemandItem.musteriNotu ? singleDemandItem.musteriNotu : '',
                        rowFullData: singleDemandItem,
                        demandCurrency: demand?.demandCurrency || 'TRY',
                        order: singleDemandItem.order || 999999, // Default high value for items without order
                    };

                    rowValues.push(rowValue);
                }
            }
        });

        // Sort rows by order value
        rowValues.sort((a, b) => a.order - b.order);

        setRowVals(rowValues);
        setRowsUpdated(isEmptyCounter > 0);

        const sampleMarkup = demand?.demandNotes ? demand?.demandNotes : '';

        try {
            let x = JSON.parse(sampleMarkup);
            setNotes(JSON.stringify(x));
        } catch (e) {
            setNotes('');
        }
    }, [props]);

    // * Form Modals
    const updateItem = (activeItemType, id, data) => {
        if (activeItemType === 'kanat')
            return <Kanat initial={data} id={id} setOpen={setOpen} projectID={projectID} demandID={demand._id} />;
        else if (activeItemType === 'diger')
            return <Diger initial={data} id={id} setOpen={setOpen} projectID={projectID} demandID={demand._id} />;
        else if (activeItemType === 'kasa')
            return <Kasa initial={data} id={id} setOpen={setOpen} projectID={projectID} demandID={demand._id} />;
        else if (activeItemType === 'uKasa')
            return <UKasa initial={data} id={id} setOpen={setOpen} projectID={projectID} demandID={demand._id} />;
        else if (activeItemType === 'pervaz')
            return <Pervaz initial={data} id={id} setOpen={setOpen} projectID={projectID} demandID={demand._id} />;
        else if (activeItemType === 'uPervaz')
            return <UPervaz initial={data} id={id} setOpen={setOpen} projectID={projectID} demandID={demand._id} />;
    };

    // ! ITEM DELETION
    const deleteItem = (activeItemType, id) => {
        if (activeItemType === 'kanat') {
            const r = window.confirm('Emin Misiniz?');
            if (r == true) {
                deleteKanat(id)
                    .then((rp) => {
                        notify(rp.data.success, rp.data.message);
                        queryClient.invalidateQueries({ queryKey: ['fetchDemand', demand._id] });
                    })
                    .catch((err) => {
                        notify(false, err.message);
                    });
            }
        } else if (activeItemType === 'diger') {
            const r = window.confirm('Emin Misiniz?');
            if (r == true) {
                deleteDiger(id)
                    .then((rp) => {
                        notify(rp.data.success, rp.data.message);
                        queryClient.invalidateQueries({ queryKey: ['fetchDemand', demand._id] });
                    })
                    .catch((err) => {
                        notify(false, err.message);
                    });
            }
        } else if (activeItemType === 'kasa') {
            const r = window.confirm('Emin Misiniz?');
            if (r == true) {
                deleteKasa(id)
                    .then((rp) => {
                        notify(rp.data.success, rp.data.message);
                        queryClient.invalidateQueries({ queryKey: ['fetchDemand', demand._id] });
                    })
                    .catch((err) => {
                        notify(false, err.message);
                    });
            }
        } else if (activeItemType === 'uKasa') {
            const r = window.confirm('Emin Misiniz?');
            if (r == true) {
                deleteUKasa(id)
                    .then((rp) => {
                        notify(rp.data.success, rp.data.message);
                        queryClient.invalidateQueries({ queryKey: ['fetchDemand', demand._id] });
                    })
                    .catch((err) => {
                        notify(false, err.message);
                    });
            }
        } else if (activeItemType === 'pervaz') {
            const r = window.confirm('Emin Misiniz?');
            if (r == true) {
                deletePervaz(id)
                    .then((rp) => {
                        notify(rp.data.success, rp.data.message);
                        queryClient.invalidateQueries({ queryKey: ['fetchDemand', demand._id] });
                    })
                    .catch((err) => {
                        notify(false, err.message);
                    });
            }
        } else if (activeItemType === 'uPervaz') {
            const r = window.confirm('Emin Misiniz?');
            if (r == true) {
                deleteUPervaz(id)
                    .then((rp) => {
                        notify(rp.data.success, rp.data.message);
                        queryClient.invalidateQueries({ queryKey: ['fetchDemand', demand._id] });
                    })
                    .catch((err) => {
                        notify(false, err.message);
                    });
            }
        }
    };

    // ! ITEM COPY
    const copyItem = (activeItemType, id, demandID) => {
        if (activeItemType === 'kanat') {
            const r = window.confirm('Emin Misiniz?');
            if (r == true) {
                copyKanat(id, demandID)
                    .then(() => {
                        queryClient.invalidateQueries({ queryKey: ['fetchDemand', demand._id] });
                    })
                    .catch((err) => {
                        console.log(err);
                    });
            }
        } else if (activeItemType === 'diger') {
            const r = window.confirm('Kopyalamak istediğinize emin misiniz?');
            if (r == true) {
                copyDiger(id, demandID)
                    .then(() => {
                        queryClient.invalidateQueries({ queryKey: ['fetchDemand', demand._id] });
                    })
                    .catch((err) => {
                        console.log(err);
                    });
            }
        } else if (activeItemType === 'kasa') {
            const r = window.confirm('Emin Misiniz?');
            if (r == true) {
                copyKasa(id, demandID)
                    .then(() => {
                        queryClient.invalidateQueries({ queryKey: ['fetchDemand', demand._id] });
                    })
                    .catch((err) => {
                        console.log(err);
                    });
            }
        } else if (activeItemType === 'uKasa') {
            const r = window.confirm('Emin Misiniz?');
            if (r == true) {
                copyUKasa(id, demandID)
                    .then(() => {
                        queryClient.invalidateQueries({ queryKey: ['fetchDemand', demand._id] });
                    })
                    .catch((err) => {
                        console.log(err);
                    });
            }
        } else if (activeItemType === 'pervaz') {
            const r = window.confirm('Emin Misiniz?');
            if (r == true) {
                copyPervaz(id, demandID)
                    .then(() => {
                        queryClient.invalidateQueries({ queryKey: ['fetchDemand', demand._id] });
                    })
                    .catch((err) => {
                        console.log(err);
                    });
            }
        } else if (activeItemType === 'uPervaz') {
            const r = window.confirm('Emin Misiniz?');
            if (r == true) {
                copyUPervaz(id, demandID)
                    .then(() => {
                        queryClient.invalidateQueries({ queryKey: ['fetchDemand', demand._id] });
                    })
                    .catch((err) => {
                        console.log(err);
                    });
            }
        }
    };

    // *  Grid History
    const openGridHistory = (activeItemType, id, data) => {
        if (activeItemType === 'uKasa') return <FormHistory formName="uKasa" id={id} />;
        if (activeItemType === 'kasa') return <FormHistory formName="kasa" id={id} />;
        if (activeItemType === 'pervaz') return <FormHistory formName="pervaz" id={id} />;
        if (activeItemType === 'uPervaz') return <FormHistory formName="uPervaz" id={id} />;
        if (activeItemType === 'kanat') return <FormHistory formName="kanat" id={id} />;
        if (activeItemType === 'diger') return <FormHistory formName="diger" id={id} />;
    };

    // ! BULK DISCOUNT CHANGE
    const handleBulkDiscountChange = () => {
        setOpenBulkDiscount(true);
    };

    const applyBulkDiscount = () => {
        const discountValue = parseFloat(bulkDiscountValue);
        if (isNaN(discountValue) || discountValue < 0 || discountValue > 100) {
            notify(false, 'Geçerli bir iskonto oranı giriniz (0-100 arası)');
            return;
        }

        // Her satır için güncelleme yap
        const updatePromises = rowVals.map((row) => {
            const id = row.id.split('-')[0];
            const type = row.id.split('-')[1];
            const obj = { indirimOrani: discountValue };

            return updateCell(type, id, obj);
        });

        Promise.all(updatePromises)
            .then(() => {
                notify(true, `Tüm ürünlerin iskonto oranı %${discountValue} olarak güncellendi`);
                setOpenBulkDiscount(false);
                setBulkDiscountValue('');
                queryClient.invalidateQueries({ queryKey: ['fetchDemand', demand._id] });
            })
            .catch((error) => {
                notify(false, 'Toplu iskonto güncellemesi sırasında hata oluştu');
                console.error(error);
            });
    };

    // * Row Reordering Handler
    const handleRowOrderChange = async (params) => {
        setLoading(true);

        try {
            // Create a copy of current rows
            const rowsClone = [...rowVals];

            // Move the row from old position to new position
            const movedRow = rowsClone.splice(params.oldIndex, 1)[0];
            rowsClone.splice(params.targetIndex, 0, movedRow);

            // Update the local state immediately for better UX
            setRowVals(rowsClone);

            // Update order values in database with silent notifications
            const updatePromises = rowsClone.map((row, index) => {
                const id = row.id.split('-')[0];
                const type = row.id.split('-')[1];
                const orderValue = index + 1; // Start from 1

                return updateCell(type, id, { order: orderValue }, true); // silent = true
            });

            await Promise.all(updatePromises);

            notify(true, 'Sıralama başarıyla güncellendi');

            // Refresh the data to ensure consistency
            queryClient.invalidateQueries({ queryKey: ['fetchDemand', demand._id] });
        } catch (error) {
            notify(false, 'Sıralama güncellenirken hata oluştu');
            console.error('Row reordering error:', error);

            // Revert the local state on error
            queryClient.invalidateQueries({ queryKey: ['fetchDemand', demand._id] });
        } finally {
            setLoading(false);
        }
    };

    const handleMenuClick = (event, rowId) => {
        setAnchorEl(event.currentTarget);
        setSelectedRowId(rowId);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedRowId(null);
    };

    const handleMenuAction = (action, params) => {
        handleMenuClose();

        switch (action) {
            case 'edit':
                setOpen(true);
                setActiveItemName(params.row.talepFormununAdi);
                setActiveItemData(params.row.rowFullData);
                setActiveItemID(params.id.split('-')[0]);
                break;
            case 'delete':
                deleteItem(params.row.talepFormununAdi, params.id.split('-')[0]);
                break;
            case 'copy':
                copyItem(params.row.talepFormununAdi, params.id.split('-')[0], demand._id);
                break;
            case 'history':
                setOpenHistory(true);
                setActiveItemName(params.row.talepFormununAdi);
                setActiveItemData(params.row.rowFullData);
                setActiveItemID(params.id.split('-')[0]);
                break;
        }
    };

    if (isLoading) return <AaroLoadingScreen />;

    if (error) return 'Bir Hata Oluştu: ' + error.message;

    // * Columns
    const columns = [
        // * google sheet url
        {
            field: 'googleSheetURL',
            headerName: 'Google',
            flex: 15,
            renderCell: (params) => (
                <MuiLink href={params.formattedValue} target="_blank" rel="noreferrer">
                    {params.formattedValue.length > 0 ? (
                        <GoogleIcon fontSize="small" sx={{ color: '#34A853' }} />
                    ) : (
                        <PowerOffIcon fontSize="small" />
                    )}
                </MuiLink>
            ),
            editable: false,
            hide: true,
        },
        // * Düzenle
        {
            field: 'edit',
            headerName: 'İşlemler',
            flex: 25,
            type: 'actions',
            getActions: (params) => [
                <IconButton
                    key="menu"
                    aria-label="menu"
                    size="small"
                    onClick={(event) => {
                        handleMenuClick(event, params.id);
                    }}
                >
                    <SettingsIcon fontSize="small" />
                </IconButton>,
            ],
        },

        // * gorsel
        {
            field: 'gorsel',
            headerName: 'Görsel',
            flex: 25,
            renderCell: (params) => (
                <div
                    style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Avatar
                        alt="Kasa"
                        variant="rounded"
                        src={
                            params.formattedValue
                                ? params.formattedValue
                                : 'https://www.kahramanlar.com.tr/wp-content/uploads/2017/08/deneme1-1.png'
                        }
                        style={{
                            width: '40px',
                            height: '40px',
                        }}
                    />
                </div>
            ),
            editable: true,
        },

        // * durumu
        {
            field: 'durumu',
            headerName: 'Durumu',
            flex: 100,
            type: 'singleSelect',
            valueOptions: tags.map((el) => el.name),
            renderCell: (params) => (
                <MDButton variant="outlined" size="small" color={getDemandStatusColor(params.formattedValue)} fullWidth>
                    {params.formattedValue}
                </MDButton>
            ),
            editable: true,
        },

        // * başlık
        {
            field: 'baslik',
            headerName: 'Başlık',
            flex: 150,
            editable: false,
            renderCell: (params) => (
                <div
                    style={{
                        whiteSpace: expandRows ? 'normal' : 'nowrap',
                        wordBreak: 'break-word',
                        lineHeight: 1.3,
                        padding: '8px 0',
                        overflow: 'hidden',
                        textOverflow: expandRows ? 'unset' : 'ellipsis',
                        display: 'flex',
                        WebkitLineClamp: expandRows ? 'unset' : 1,
                        WebkitBoxOrient: expandRows ? 'unset' : 'vertical',
                        width: '100%',
                        height: '100%',
                        alignItems: 'center',
                        justifyContent: expandRows ? 'center' : 'flex-start',
                        textAlign: expandRows ? 'center' : 'left',
                    }}
                >
                    {params.value}
                </div>
            ),
        },

        // * kalınlık
        {
            field: 'kalinlik',
            headerName: 'Kalınlık (cm)',
            flex: 50,
            type: 'number',
            editable: false,
            hide: true,
        },
        // * en
        {
            field: 'en',
            headerName: 'En (cm)',
            flex: 50,
            type: 'number',
            editable: false,
            hide: true,
        },
        // * boy
        {
            field: 'boy',
            headerName: 'Boy (cm)',
            flex: 50,
            type: 'number',
            editable: false,
            hide: true,
        },
        // * miktar
        {
            field: 'miktar',
            headerName: 'Miktar',
            flex: 50,
            type: 'number',
            editable: true,
        },
        // * birim fiyatı
        {
            field: 'verilenFiyat',
            headerName: 'Birim Fiyatı',
            flex: 50,
            type: 'number',
            valueGetter: (value, row) => {
                if (!row) return '';

                const valueFormatted = formatPrice(
                    calculateDiscount(row.verilenFiyat, 1, row.indirimOrani),
                    demand?.demandCurrency || 'TRY'
                );

                return `${valueFormatted}`;
            },
            editable: user.role == 'superadmin' ? true : false,
        },
        // * indirimOrani
        {
            field: 'indirimOrani',
            headerName: 'İndirim',
            flex: 50,
            type: 'number',
            valueGetter: (value) => {
                if (value == null) return '';
                const valueFormatted = Number(value).toLocaleString();
                return `${valueFormatted} %`;
            },
            renderCell: (params) => {
                const discountValue = params.row.indirimOrani || 0;
                const isHighDiscount = discountValue > 40;

                return (
                    <div
                        style={{
                            backgroundColor: isHighDiscount ? '#ffebee' : 'transparent',
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '0 8px',
                            borderRadius: '4px',
                        }}
                    >
                        {params.formattedValue}
                    </div>
                );
            },
            editable: true,
        },
        // * toplamı
        {
            field: 'toplam',
            headerName: 'Toplam',
            flex: 70,
            type: 'number',
            valueGetter: (value, row) => {
                if (!row) return '';

                if (row.verilenFiyat == null || row.miktar == null) return '-';

                const indirimOrani = row.indirimOrani ?? 0;
                const currency = demand?.demandCurrency ?? 'TRY';

                const valueFormatted = formatPrice(
                    calculateDiscount(row.verilenFiyat, row.miktar, indirimOrani),
                    currency
                );

                return `${valueFormatted}`;
            },
            editable: false,
        },

        // * musteriNotu
        {
            field: 'musteriNotu',
            headerName: 'Müşterimize Notumuz',
            flex: 150,
            type: 'string',
            editable: true,
            hide: true,
        },
    ];

    // * Calculate Total Sum
    const SumOfGridRows = () => {
        let total = 0;

        // Loop through all rows and calculate total
        rowVals.forEach((row) => {
            if (row.verilenFiyat && row.miktar) {
                total += calculateDiscount(row.verilenFiyat, row.miktar, row.indirimOrani);
            }
        });

        return formatPrice(total, demand.demandCurrency ? demand.demandCurrency : 'TRY');
    };

    // Custom Footer Component
    const CustomFooter = () => (
        <MDBox>
            <TableFooter
                style={{
                    display: 'flex',
                    flexDirection: 'row-reverse',
                }}
            >
                <TableRow>
                    <TableCell colSpan={12}>
                        <strong>
                            <p style={{ fontSize: '14px' }}>Toplam: {SumOfGridRows()}</p>
                        </strong>
                    </TableCell>
                </TableRow>
            </TableFooter>
        </MDBox>
    );

    return (
        <MDBox>
            {rowsUpdated ? (
                <MDBox color="white" bgColor="white" variant="gradient" borderRadius="lg" shadow="lg" opacity={1} p={2}>
                    <DataGridPro
                        autoPageSize
                        disableRowSelectionOnClick
                        rows={rowVals}
                        columns={columns}
                        processRowUpdate={processRowUpdate}
                        onProcessRowUpdateError={handleProcessRowUpdateError}
                        rowHeight={expandRows ? 140 : 52}
                        showToolbar
                        loading={loading}
                        rowReordering
                        onRowOrderChange={handleRowOrderChange}
                        slots={{
                            footer: CustomFooter,
                            toolbar: CustomToolbar,
                        }}
                        slotProps={{
                            toolbar: {
                                expandRows,
                                setExpandRows,
                                onBulkDiscountChange: handleBulkDiscountChange,
                            },
                        }}
                        initialState={{
                            columns: {
                                columnVisibilityModel: {
                                    googleSheetURL: false,
                                    musteriNotu: false,
                                    kalinlik: false,
                                    en: false,
                                    boy: false,
                                },
                            },
                        }}
                        sx={(theme) => ({
                            fontSize: pxToRem(12),
                            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                            fontWeight: 400,
                            lineHeight: 1.25,
                            [`& .${gridClasses.row}`]: {
                                bgcolor: 'var(--grey-unknown)',
                                ...theme.applyStyles('light', {
                                    bgcolor: 'var(--grey-unknown)',
                                }),
                            },
                            '--grey-unknown': grey[50],
                            ...(expandRows && {
                                '& .MuiDataGrid-cell': {
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    textAlign: 'center',
                                },
                                '& .MuiDataGrid-cell--textLeft': {
                                    justifyContent: 'center',
                                },
                                '& .MuiDataGrid-cell--textRight': {
                                    justifyContent: 'center',
                                },
                                '& .MuiDataGrid-cell--textCenter': {
                                    justifyContent: 'center',
                                },
                            }),
                        })}
                    />

                    <Modal open={open} onClose={() => setOpen(false)}>
                        <div style={{ ...modalStyle, ...cellStyles.paper(theme) }}>
                            {updateItem(activeItemName, activeItemID, activeItemData)}
                        </div>
                    </Modal>

                    {openComparision && (
                        <Comparision
                            activeItemType={activeItemName}
                            initial={activeItemData}
                            id={activeItemID}
                            open={openComparision}
                            handleClose={handleClose}
                            projectName={projectName}
                            projectID={projectID}
                        />
                    )}

                    <Modal open={openHistory} onClose={() => setOpenHistory(false)}>
                        <div style={{ ...modalStyle, ...cellStyles.paper(theme) }}>
                            {openGridHistory(activeItemName, activeItemID, activeItemData)}
                        </div>
                    </Modal>

                    {/* Bulk Discount Modal */}
                    <Modal open={openBulkDiscount} onClose={() => setOpenBulkDiscount(false)}>
                        <Box
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: 400,
                                bgcolor: 'background.paper',
                                border: '2px solid #000',
                                boxShadow: 24,
                                p: 4,
                                borderRadius: 2,
                            }}
                        >
                            <MDTypography variant="h6" component="h2" mb={2}>
                                Toplu İskonto Değiştir
                            </MDTypography>
                            <MDTypography variant="body2" mb={3}>
                                Tüm ürünlerin iskonto oranını aynı değere güncelleyin:
                            </MDTypography>
                            <TextField
                                fullWidth
                                label="İskonto Oranı (%)"
                                type="number"
                                value={bulkDiscountValue}
                                onChange={(e) => setBulkDiscountValue(e.target.value)}
                                inputProps={{ min: 0, max: 100, step: 0.1 }}
                                sx={{ mb: 3 }}
                            />
                            <Stack direction="row" spacing={2} justifyContent="flex-end">
                                <MDButton
                                    variant="outlined"
                                    color="secondary"
                                    onClick={() => {
                                        setOpenBulkDiscount(false);
                                        setBulkDiscountValue('');
                                    }}
                                >
                                    İptal
                                </MDButton>
                                <MDButton variant="gradient" color="info" onClick={applyBulkDiscount}>
                                    Uygula
                                </MDButton>
                            </Stack>
                        </Box>
                    </Modal>
                </MDBox>
            ) : (
                <MDBox bgColor="white" variant="gradient">
                    <MDTypography variant="h6" fontWeight="medium">
                        Projeye kalem girilmemiş
                    </MDTypography>
                </MDBox>
            )}

            {/* Dropdown Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <MenuItem
                    onClick={() => {
                        const params = rowVals.find((row) => row.id === selectedRowId);
                        if (params) handleMenuAction('edit', { id: selectedRowId, row: params });
                    }}
                >
                    <ListItemIcon>
                        <EditIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Düzenle" />
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        const params = rowVals.find((row) => row.id === selectedRowId);
                        if (params) handleMenuAction('delete', { id: selectedRowId, row: params });
                    }}
                >
                    <ListItemIcon>
                        <DeleteIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Sil" />
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        const params = rowVals.find((row) => row.id === selectedRowId);
                        if (params) handleMenuAction('copy', { id: selectedRowId, row: params });
                    }}
                >
                    <ListItemIcon>
                        <ContentCopyIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Kopyala" />
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        const params = rowVals.find((row) => row.id === selectedRowId);
                        if (params) handleMenuAction('history', { id: selectedRowId, row: params });
                    }}
                >
                    <ListItemIcon>
                        <HistoryIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Geçmiş" />
                </MenuItem>
            </Menu>
        </MDBox>
    );
}
